import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card } from '@mui/material';

import { EmptyBox } from '@europrocurement/flexy-components';
import { domainIcon, formalityIcon, offerPackageIcon, tagIcon } from '@europrocurement/l2d-icons';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useOffreService } from '@europrocurement/l2d-domain';
import { PackageJsonldPackageRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';

import Form from './Form/Form';
import { packageFichePath, packageListPath } from './routes';
import FicheHeader, { type FicheSubtitleElement } from '../components/fiche/FicheHeader';
import buildCreationTitle from '../components/fiche/buildCreationTitle';
import LoadingScreen from '../forms/LoadingScreen';

const PACKAGE_SECTION_LABEL = 'Configuration des offres commerciales';

/**
 * Fiche d'un package sur route dédiée (remplace l'édition/création en modale).
 * - `admin/offres/package/new` : création.
 * - `admin/offres/package/:id` : édition (le package est chargé par son identifiant).
 */
const PackageFiche: React.FunctionComponent = function () {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id || id === 'new';

    const { offerPackageModel: model } = useOffreService();
    const { request } = useApiRequest<PackageJsonldPackageRead>();

    const [entity, setEntity] = useState<PackageJsonldPackageRead | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(!isCreate);
    const [isInError, setIsInError] = useState<boolean>(false);
    // Libellé saisi dans le formulaire (mode création), affiché dans l'en-tête.
    const [draftLabel, setDraftLabel] = useState<string>('');

    useEffect(() => {
        let active = true;
        if (!isCreate) {
            setIsLoading(true);
            setIsInError(false);

            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => {
                    if (active) {
                        setEntity(result.data);
                    }
                },
                failureCallback: () => {
                    if (active) {
                        setIsInError(true);
                    }
                },
            }).finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        }

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isCreate]);

    const goToList = useCallback(() => {
        navigate(packageListPath);
    }, [navigate]);

    // Après un enregistrement :
    // - en création : on ouvre la fiche du nouvel objet ;
    // - en édition : on reste sur la fiche et on rafraîchit l'entité
    //   (sans toggler le chargement, pour ne pas démonter le formulaire).
    const handleAfterSubmit = useCallback(
        (createdId?: number) => {
            if (isCreate) {
                if (createdId != null) {
                    navigate(packageFichePath(createdId));
                }
                return;
            }
            if (!id) {
                return;
            }
            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => setEntity(result.data),
            });
        },
        [id, isCreate, model, navigate, request],
    );

    const title = isCreate
        ? buildCreationTitle("Création d'un package", draftLabel)
        : (entity?.nomCommercial ?? entity?.libelle ?? 'Package');

    const subtitleElements: FicheSubtitleElement[] =
        isCreate || !entity
            ? []
            : [
                  { icon: tagIcon.props.icon, label: `Libellé : ${entity.libelle}` },
                  {
                      icon: domainIcon.props.icon,
                      label: `Domaine : ${entity.domaine?.libelle}`,
                  },
                  {
                      icon: formalityIcon.props.icon,
                      label: `Formalité : ${entity.formalite?.libelle}`,
                      link: entity.formalite?.id
                          ? `/formalites/liste/${entity.formalite.id}`
                          : undefined,
                      linkTooltip: 'Ouvrir la formalité',
                  },
              ];

    const renderContent = () => {
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isInError || (!isCreate && !entity)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '30vh',
                    }}
                >
                    <EmptyBox
                        text="Package introuvable"
                        subText="Ce package n'existe pas ou n'a pas pu être chargé."
                    />
                </Box>
            );
        }

        return (
            <Form
                isCreate={isCreate}
                entity={entity}
                afterSubmit={handleAfterSubmit}
                onCancel={goToList}
                onLabelChange={setDraftLabel}
            />
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <FicheHeader
                icon={offerPackageIcon}
                title={title}
                reference={isCreate ? undefined : entity?.id}
                subtitleElements={subtitleElements}
                sectionLabel={PACKAGE_SECTION_LABEL}
                listPath={packageListPath}
            />

            <Card sx={{ marginTop: 2, padding: 3 }}>{renderContent()}</Card>
        </Box>
    );
};

export default PackageFiche;
